import { useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { CONNECT_SCOPES } from '@/auth/scopes';
import { isAuthConfigured } from '@/auth/msalConfig';
import { FirstRunConsent } from './FirstRunConsent';
import { SetupNotice } from './SetupNotice';
import type { ReactNode } from 'react';

/**
 * C1 — gates the app behind sign-in. Shows the transparency screen until the admin
 * connects, then renders the authenticated tree.
 */
export function ConnectGate({ children }: { children: ReactNode }) {
  const { instance } = useMsal();
  const isAuthed = useIsAuthenticated();
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function connect() {
    setConnecting(true);
    setError(null);
    try {
      const result = await instance.loginPopup({ scopes: [...CONNECT_SCOPES] });
      if (result.account) instance.setActiveAccount(result.account);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sign-in failed.');
    } finally {
      setConnecting(false);
    }
  }

  // No real client ID configured — show setup guidance instead of a broken request.
  if (!isAuthConfigured) return <SetupNotice />;

  if (isAuthed) return <>{children}</>;

  return (
    <>
      <FirstRunConsent onConnect={connect} connecting={connecting} />
      {error && (
        <div className="notice notice--error" role="alert">
          <strong>Sign-in failed</strong>
          <p>{error}</p>
        </div>
      )}
    </>
  );
}
